import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';

declare var $: any;

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit {
  isMe : any;
  isForm : any;
  isProfileOpen : boolean = false;
  isSaving : boolean = false;

  constructor(
    private fb: FormBuilder,
    public authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
    private toastr: ToastrService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.sessionInfo();
    this.resetForm();
    // restore the collapsed-rail preference
    if (localStorage.getItem('hatam-minimenu') === '1') {
      document.body.classList.add('minimenu');
    }
    this.stripThemeNavClasses();
  }

  // the theme's JS stamps `enlarge-menu` on the body below 1300px, whose GLOBAL
  // css hides the sidenav and zeroes the content margin — it fights our
  // rail/drawer system, so it is stripped on load and after every resize
  // (our listener runs after the theme's, so we always get the last word)
  @HostListener('window:resize')
  stripThemeNavClasses() {
    document.body.classList.remove('enlarge-menu', 'enlarge-menu-all');
  }

  isMini() {
    return document.body.classList.contains('minimenu');
  }

  // ===== light / dark theme =====
  isDark() {
    return document.body.classList.contains('theme-dark');
  }

  toggleTheme() {
    const dark = document.body.classList.toggle('theme-dark');
    localStorage.setItem('hatam-theme', dark ? 'dark' : 'light');
    // charts & co. re-read their token colors on this signal
    window.dispatchEvent(new CustomEvent('hatam-theme'));
  }

  toggleSidenav() {
    if (window.innerWidth <= 1024) {
      // small screens: the sidenav is a slide-in drawer, not a rail
      document.body.classList.toggle('mobile-nav-open');
      return;
    }
    const collapsed = document.body.classList.toggle('minimenu');
    localStorage.setItem('hatam-minimenu', collapsed ? '1' : '0');
  }

  sessionInfo(){
    this.isMe = this.authService.getSession();
  }

  roleLabel(){
    if(String((this.isMe && this.isMe.role) || '') === '0'){ return 'Super Admin'; }
    return (this.isMe && this.isMe.roleInfo && this.isMe.roleInfo.name) || 'Admin';
  }

  // initials avatar — honest, unlike the theme's stock photo
  initials(){
    const parts = String((this.isMe && this.isMe.name) || '?').trim().split(/\s+/);
    return parts.slice(0, 2).map((p: string) => p.charAt(0)).join('').toUpperCase();
  }

  resetForm(){
    const me = this.authService.getSession() || {};
    this.isForm = this.fb.group({
      name : new FormControl(me.name || '', [Validators.required]),
      email : new FormControl(me.email || '', [Validators.required, Validators.email]),
      username : new FormControl(me.username || '', [Validators.required]),
      new_password : new FormControl('', [Validators.minLength(6)]),
      current_password : new FormControl(''),
    });
  }

  // ===== profile dialog =====
  openProfile(){
    this.resetForm();
    this.isProfileOpen = true;
    document.body.style.overflow = 'hidden'; // lock the page scroll behind the dialog
  }

  closeProfile(){
    if(this.isSaving){ return; } // never close while the save is in flight
    this.isProfileOpen = false;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscape(){
    if(this.isLogoutOpen){ this.closeLogout(); return; }
    if(this.isProfileOpen){ this.closeProfile(); }
  }

  // show inline errors once a field is touched / after a submit attempt
  isInvalid(control : string){
    const c = this.isForm.get(control);
    return c && c.invalid && (c.touched || c.dirty);
  }

  needsCurrentPassword(){
    return !!this.isForm.value.new_password && !this.isForm.value.current_password;
  }

  async onSubmit(){
    try{
      if(this.isForm.invalid || this.needsCurrentPassword()){
        this.isForm.markAllAsTouched();
        return;
      }
      if(this.isSaving){ return; }
      this.isSaving = true;
      const payload = this.isForm.getRawValue();
      this.apiService.methodPutSilent(`admin/profile`, payload, this.authService.getToken()).subscribe(async (response)=>{
        this.isSaving = false;
        if(response && response.status == 200){
          // keep the stored session in sync so the navbar & forms show the new identity
          this.authService.updateSession({
            name: response.data.name,
            email: response.data.email,
            username: response.data.username,
          });
          this.sessionInfo();
          this.isProfileOpen = false;
          document.body.style.overflow = '';
          await this.toastr.success(`${response.data.name}`, `Profil diperbarui`);
        }else{
          // failure: the dialog stays open with the input intact
          await this.toastr.error((response && response.message) || `Periksa isian lalu coba lagi.`, `Gagal menyimpan profil`);
        }
      }, async () => {
        this.isSaving = false;
        await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menyimpan profil`);
      });
    }catch(error){
      this.isSaving = false;
      await this.toastr.error(`Periksa isian lalu coba lagi.`, `Gagal menyimpan profil`);
    }
  }

  // ===== logout confirmation (custom dialog, not SweetAlert) =====
  isLogoutOpen : boolean = false;

  openLogout(){
    this.isLogoutOpen = true;
    document.body.style.overflow = 'hidden';
    setTimeout(() => { // focus Cancel so the keyboard lands inside the dialog
      const btn: any = document.querySelector('.nl-dialog .vl-btn-ghost');
      if(btn){ btn.focus(); }
    });
  }

  closeLogout(){
    this.isLogoutOpen = false;
    document.body.style.overflow = '';
  }

  confirmLogout(){
    this.closeLogout();
    this.authService.logout();
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  isStoppingImpersonate : boolean = false;
  stopImpersonate(){
    if(this.isStoppingImpersonate){ return; }
    this.isStoppingImpersonate = true;
    this.authService.stopImpersonate().subscribe((response) => {
      this.isStoppingImpersonate = false;
      if(response && response.status == 200){
        window.location.href = '/administrator'; // full reload — every screen must pick up the restored session
      }
    }, () => { this.isStoppingImpersonate = false; });
  }
}
